/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

/**
 * Memoized forecasting service that caches results for performance optimization
 * Wraps the base ForecastingService with intelligent caching capabilities
 */
class MemoizedForecastingService(
    private val maxCacheSize: Int = 100,
    private val baseService: ForecastingService = ForecastingService(),
) {
    private val cache = mutableMapOf<String, ForecastingResult>()
    private val keyGenerator = ForecastingCacheKeyGenerator()
    private var totalRequests = 0
    private var cacheHits = 0

    /**
     * Forecast with algorithm using memoization
     */
    fun forecastWithAlgorithm(
        algorithmName: String,
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        totalRequests++

        val cacheKey = keyGenerator.generateKey(algorithmName, data, periods)

        // Check cache first
        cache[cacheKey]?.let { cachedResult ->
            cacheHits++
            return cachedResult
        }

        // Compute new result
        val result = baseService.forecastWithAlgorithm(algorithmName, data, periods)

        // Store in cache with size management
        if (cache.size >= maxCacheSize) {
            evictOldestEntry()
        }

        cache[cacheKey] = result
        return result
    }

    /**
     * Forecast with best algorithm using memoization
     */
    fun forecastWithBestAlgorithm(
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        totalRequests++

        val cacheKey = keyGenerator.generateKeyForBestAlgorithm(data, periods)

        // Check cache first
        cache[cacheKey]?.let { cachedResult ->
            cacheHits++
            return cachedResult
        }

        // Compute new result
        val result = baseService.forecastWithBestAlgorithm(data, periods)

        // Store in cache with size management
        if (cache.size >= maxCacheSize) {
            evictOldestEntry()
        }

        cache[cacheKey] = result
        return result
    }

    /**
     * Forecast with multiple algorithms using memoization
     */
    fun forecastWithMultipleAlgorithms(
        algorithmNames: List<String>,
        data: List<Double>,
        periods: Int,
    ): List<ForecastingResult> =
        algorithmNames.map { algorithmName ->
            forecastWithAlgorithm(algorithmName, data, periods)
        }

    /**
     * Get all available algorithms
     */
    fun getAvailableAlgorithms(): List<String> = baseService.getAvailableAlgorithms()

    /**
     * Recommend algorithm with caching
     */
    fun recommendAlgorithm(data: List<Double>): AlgorithmRecommendation {
        totalRequests++

        val cacheKey = keyGenerator.generateKeyForRecommendation(data)

        // For recommendation caching, we use a separate cache approach
        // since recommendations are lightweight computations
        return baseService.recommendAlgorithm(data)
    }

    /**
     * Compare algorithms with selective caching
     */
    fun compareAlgorithms(
        data: List<Double>,
        periods: Int,
    ): List<ForecastingResult> =
        getAvailableAlgorithms()
            .map { algorithmName ->
                forecastWithAlgorithm(algorithmName, data, periods)
            }.sortedByDescending { it.confidence }

    /**
     * Benchmark algorithms (not cached due to complexity)
     */
    fun benchmarkAlgorithms(
        trainingData: List<Double>,
        testData: List<Double>,
    ): List<AlgorithmBenchmark> = baseService.benchmarkAlgorithms(trainingData, testData)

    /**
     * Optimize algorithm parameters (not cached due to variability)
     */
    fun optimizeAlgorithmParameters(
        algorithmName: String,
        data: List<Double>,
    ): OptimizationResult = baseService.optimizeAlgorithmParameters(algorithmName, data)

    /**
     * Register custom algorithm
     */
    fun registerCustomAlgorithm(
        name: String,
        algorithm: ForecastingAlgorithm,
    ) {
        baseService.registerCustomAlgorithm(name, algorithm)
        // Clear cache as available algorithms changed
        clearCache()
    }

    /**
     * Get cache size
     */
    fun getCacheSize(): Int = cache.size

    /**
     * Get cache hit rate
     */
    fun getCacheHitRate(): Double = if (totalRequests > 0) cacheHits.toDouble() / totalRequests else 0.0

    /**
     * Clear the cache
     */
    fun clearCache() {
        cache.clear()
        totalRequests = 0
        cacheHits = 0
    }

    /**
     * Get cache statistics
     */
    fun getCacheStatistics(): CacheStatistics =
        CacheStatistics(
            size = cache.size,
            maxSize = maxCacheSize,
            hitRate = getCacheHitRate(),
            totalRequests = totalRequests,
            cacheHits = cacheHits,
        )

    private fun evictOldestEntry() {
        if (cache.isNotEmpty()) {
            val oldestKey = cache.keys.first()
            cache.remove(oldestKey)
        }
    }
}

/**
 * Cache key generator for consistent hashing of forecast requests
 */
class ForecastingCacheKeyGenerator {
    fun generateKey(
        algorithmName: String,
        data: List<Double>,
        periods: Int,
    ): String {
        val dataHash = generateDataHash(data)
        return "forecast:$algorithmName:$dataHash:$periods"
    }

    fun generateKeyForBestAlgorithm(
        data: List<Double>,
        periods: Int,
    ): String {
        val dataHash = generateDataHash(data)
        return "best:$dataHash:$periods"
    }

    fun generateKeyForRecommendation(data: List<Double>): String {
        val dataHash = generateDataHash(data)
        return "recommend:$dataHash"
    }

    private fun generateDataHash(data: List<Double>): String {
        if (data.isEmpty()) return "empty"

        // Create a stable hash of the data
        val sum = data.sum()
        val mean = data.average()
        val size = data.size
        val first = data.firstOrNull() ?: 0.0
        val last = data.lastOrNull() ?: 0.0

        // Simple but stable hash combining key characteristics
        val hashComponents =
            listOf(
                sum.toString(),
                mean.toString().take(10), // Limit precision for stability
                size.toString(),
                first.toString(),
                last.toString(),
            )

        return hashComponents.joinToString(":") {
            it.replace(".", "_") // Make safe for key format
        }
    }
}

/**
 * Cache statistics data class
 */
data class CacheStatistics(
    val size: Int,
    val maxSize: Int,
    val hitRate: Double,
    val totalRequests: Int,
    val cacheHits: Int,
)
